import { CircleNotch } from "@phosphor-icons/react";
import { cva } from "class-variance-authority";
import clsx from "clsx";

const circleVariants = cva(
  "relative flex items-center justify-center rounded-full",
  {
    variants: {
      size: {
        sm: "size-6 text-button-xxs",
        md: "size-10 text-xs-bold",
      },
    },
    defaultVariants: {
      size: "md",
    },
  },
);

type ProgressCircleProps = {
  value: number;
  isLoading: boolean;
  size?: "sm" | "md";
  className?: string;
};

export default function OnboardingProgressCircle({
  value,
  isLoading,
  size = "md",
  className,
}: ProgressCircleProps) {
  const max = 4;
  const isSmall = size === "sm";
  const segmentSweep = 80;

  const svgSize = isSmall ? 24 : 40;
  const sizeClassName = isSmall ? "!size-6" : "size-10";
  const radius = isSmall ? 11.5 : 18;
  const strokeBase = isSmall ? 2.5 : 3.5;
  const strokeProgress = isSmall ? 1.5 : 2;

  const degToRad = (deg: number) => (deg * Math.PI) / 180;

  const segments = Array.from({ length: max }, (_, i) => ({
    start: i * (360 / max),
    end: i * (360 / max) + segmentSweep,
  }));

  const describeArc = (start: number, end: number) => {
    const center = svgSize / 2;
    const startAngle = degToRad(start - 90);
    const endAngle = degToRad(end - 90);

    const x1 = center + radius * Math.cos(startAngle);
    const y1 = center + radius * Math.sin(startAngle);
    const x2 = center + radius * Math.cos(endAngle);
    const y2 = center + radius * Math.sin(endAngle);

    const largeArc = end - start <= 180 ? 0 : 1;

    return `M${x1} ${y1} A${radius} ${radius} 0 ${largeArc} 1 ${x2} ${y2}`;
  };

  return (
    //this extra div allows us to position the circle absolutely
    <div className={className}>
      <div className={circleVariants({ size })}>
        <svg
          width={svgSize}
          height={svgSize}
          viewBox={`0 0 ${svgSize} ${svgSize}`}
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
          className={clsx(sizeClassName, "absolute overflow-visible")}
        >
          <title>Onboarding Progress</title>

          {/* Base ring */}
          <circle
            cx={svgSize / 2}
            cy={svgSize / 2}
            r={radius}
            fill="none"
            className="stroke-purple-200"
            strokeWidth={strokeBase}
          />

          {/* Progress arcs */}
          {!isLoading && (
            <g className="[filter:drop-shadow(0_0_6px_rgba(119,85,255,0.7))]">
              {segments.slice(0, value).map(({ start, end }) => (
                <path
                  key={`${start}_${end}`}
                  d={describeArc(start, end)}
                  stroke="#7755FF"
                  strokeWidth={strokeProgress}
                  strokeLinecap="round"
                  fill="none"
                />
              ))}
            </g>
          )}
        </svg>

        {/* Label */}
        <div className="bg-white inline-flex items-center justify-center size-full rounded-full">
          {isLoading ? (
            <CircleNotch
              size={isSmall ? 10 : 16}
              className="animate-spin text-purple-500"
            />
          ) : (
            <>
              <span
                id="progress-value-label"
                className={clsx(
                  value === 0 ? "text-purple-300" : "text-purple-950",
                )}
              >
                {value}
              </span>
              <span
                id="progress-max-value-label"
                className={clsx(
                  isSmall
                    ? "text-purple-600"
                    : value === max
                      ? "text-purple-600"
                      : "text-purple-500",
                )}
              >
                /{max}
              </span>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
